import * as Sentry from '@sentry/react';
import parentWindowDataChannel from '../data/ParentWindowDataChannel';

/**
 * @description Handles the fetching of the JWT and maintaining the session to ensure the user
 *              always has a valid Bearer Token to communicate with Daisy.
 * @typedef {object} JWTokenHandler
 * @property {() => Promise<string|{error: Error}>} init
 * @property {() => Promise<void>} ensureAccessToken
 * @property {string} jwt
 * @property {() => void} maintainSession
 * @property {() => void} clearSessionInterval
 */

/** @implements JWTokenHandler */
class JWTokenHandler {
    /** @type {string|null} */
    #jwt = null;

    /** @type {boolean} */
    #verbose = false;

    /** @type {NodeJS.Interval} */
    #intervalId = null;

    /** @type {number} */
    #sessionExpiry = null;

    /**
     * @param {{verbose?: boolean, interval?: number}} options
     * @constructor
     */
    constructor(options) {
        const defaultInterval =
            process.env.REACT_APP_ENVIRONMENT !== 'production'
                ? 1000 * 90 // 90 seconds
                : 1000 * 60 * 60 * 1; // 1 hour
        this.#verbose = options?.verbose ?? false;
        this.#sessionExpiry = options?.interval ?? defaultInterval;
    }

    /**
     * @returns {Promise<string|{error: Error}>}
     */
    async init() {
        // Avoid fetching a new JWT, unless it's the initial request
        // or the existing JWT is expired.
        // Get the JWT from Ghost Admin client
        // This causes Ghost Admin to make a request to `/identities`
        this.#jwt = await parentWindowDataChannel.getToken();

        if (this.#jwt === null) {
            // User is not the owner and doesn't have permission to read the data.
            // Stop with any further action
            const error = new Error('Only the site owner can access billing information.');
            return { error };
        }

        return await this.#fetchBillingSession({ init: true });
    }

    /**
     * @private
     * @param {{init?: boolean}} options
     * @returns {Promise<string|{error: Error}>}
     */
    async #fetchBillingSession({ init = false }) {
        try {
            const res = await window.fetch('/api/billing-session', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    jwt: this.#jwt,
                    options: {
                        init,
                        verbose: this.#verbose,
                    },
                }),
            });

            const session = await res.json();

            // When the JWT is expired, we need to fetch a new one, which can be done
            // by passing the refresh option to this function
            if (session?.error && session?.error?.message === 'Token has expired') {
                Sentry.addBreadcrumb({
                    category: 'JWTokenHandler_fetchBillingSession',
                    message: 'JWT expired',
                    level: 'debug',
                });

                return await this.init();
            }

            if (
                session?.error &&
                session?.error?.status === 403 &&
                session?.error?.message.match(/legacy plan/gim)
            ) {
                Sentry.addBreadcrumb({
                    category: 'JWTokenHandler_fetchBillingSession',
                    message: `Legacy plan: ${session?.error?.message}`,
                    level: 'debug',
                });

                if (this.#verbose) {
                    console.log('Legacy', session.error);
                }

                return { error: session?.error };
            } else if (session?.error && session?.error?.status === 403) {
                Sentry.addBreadcrumb({
                    category: 'JWTokenHandler_fetchBillingSession',
                    message: `Unauthorized: ${session.error}`,
                    level: 'debug',
                });

                if (this.#verbose) {
                    console.log('Unauthorized', session.error);
                }

                const error = new Error('Only the site owner can access billing information.');
                return { error };
            } else if (session?.error) {
                if (this.#verbose) {
                    console.log('No permission', session.error);
                }

                const error = new Error(
                    'Error receiving billing data, try again or contact support@ghost.org',
                );

                Sentry.captureException(error, {
                    contexts: {
                        api_response: session,
                        full_error: session?.error,
                    },
                    extra: {
                        initialRequest: init,
                    },
                    tags: {
                        source: 'JWTokenHandler',
                        api_response_status: session?.error?.status,
                    },
                });

                return { error };
            }

            return this.#jwt;
        } catch (error) {
            Sentry.captureException(error, {
                extra: {
                    initialRequest: init,
                },
                tags: {
                    source: 'JWTokenHandler',
                },
            });
            return { error };
        }
    }

    /**
     * @description This method is used to ensure that the JWT is valid and not expired and the cookie is set
     * @returns {Promise<void>}
     */
    async ensureAccessToken() {
        await this.#fetchBillingSession({ init: false });
    }

    /**
     * @readonly
     * @returns {string|null}
     */
    get jwt() {
        return this.#jwt;
    }

    /**
     * @returns {Promise<string|{error: Error}>}
     */
    async getJWT() {
        if (!this.#jwt) {
            return await this.init();
        }
        return this.#jwt;
    }

    /**
     * @description This method is used to maintain the session cookie by refreshing the JWT every 4 hours
     * @returns {void}
     */
    maintainSession = () => {
        if (this.#verbose) {
            console.log(`[API] maintainSession every ${this.#sessionExpiry / 1000} seconds`);
        }
        // Refresh the JWT every 4 hours
        this.#intervalId = setInterval(async () => {
            if (this.#verbose) {
                console.log('[API] Refreshing JWT', new Date().toISOString());
            }
            Sentry.addBreadcrumb({
                category: 'JWTokenHandler_maintainSession',
                message: `Refreshing JWT`,
                level: 'debug',
            });
            await this.ensureAccessToken();
        }, this.#sessionExpiry);
    };

    /**
     * @description This method is used to clear the session interval
     * @returns {void}
     */
    clearSessionInterval = () => {
        if (this.#verbose) {
            console.log('[API] Clear session interval');
        }
        if (this.#intervalId) {
            clearInterval(this.#intervalId);

            Sentry.addBreadcrumb({
                category: 'JWTokenHandler_clearSessionInterval',
                message: `Cleared session maintaining interval`,
                level: 'debug',
            });
        }
    };
}

export default JWTokenHandler;
